/**
 * <quick-registration> — CITY-5336.
 *
 * Two modes wired to Catalin's RPCs:
 *   signup → POST /rpc/quick-registration   (creates user, auto-logs them in)
 *   signin → POST /rpc/magic-link-request   (emails a magic-link JWT)
 *
 * No OTP step, no password collection, no email-existence probe (the BE
 * deliberately doesn't expose one — see RPCFormMagicLinkRequest header).
 *
 * Social auth (Google / Apple) all post to /rpc/quick-registration.
 * Catalin's HRegistration::pickRegistrationScenario routes by the fb/google/apple
 * payload presence, so this is a single endpoint for every signup variant.
 */
angular.module('quickRegistrationModule').component('quickRegistration', {
	templateUrl: 'quickRegistration/quickRegistration.html',
	bindings: {
		// RPC URLs
		magicLinkUrl: '@',          // /rpc/magic-link-request
		quickRegistrationUrl: '@',  // /rpc/quick-registration

		// Display variant — title/subtitle copy is driven by the popup
		// wrapper from the trigger button. The flow itself is unified
		// (email-first, then magic-link or signup based on BE response).
		// Aliased: native HTML `title` on a custom element becomes a browser tooltip that inherits to children.
		title: '<headerTitle',
		subtitle: '<',

		// Context tokens forwarded to BE so post-login actions complete
		listingToken: '<',
		buildingToken: '<',
		listingAlertOnBuildingToken: '<',
		searchToken: '<',
		regTrigger: '<',

		// CITY-5339 — show the name field immediately (instead of after the
		// BE confirms the email is new). Used by the inline form on the guest
		// My Saved Searches tab; when set and the name is already valid, a
		// new-user signup completes in a single Continue click.
		nameUpfront: '<',
		pageId: '@',
		calloutBoxType: '<',
		calloutBoxSourcePage: '<',

		// Page context
		paramSrc: '@',
		paramTier: '@',
		paramKw: '@',
		listingMaskId: '@',
		buildingId: '@',
		historyId: '@',
		articleId: '@',
		detectedUserType: '@',

		// CITY-5336 — registration page-type slug (reporting dimension) from
		// WRegistrationPopupV2. Threaded through every submit via _buildPayload
		// so the BE stores usr_registration_extra_info.reg_page_type.
		registrationPageType: '@',

		// CITY-5336 — server-computed registration version (QUICK / R-B / R-G ...)
		// from WRegistrationPopupV2. Threaded through every submit (email + OAuth)
		// via _buildPayload so the BE records used_reg_version correctly.
		regVersion: '@',

		// Social auth
		googleClientId: '@',
		appleClientId: '@',
		appleRedirectPath: '@',
		appleNonce: '@',
		showSocial: '<',
		oneTapEnabled: '<',

		// Legal
		termsOfUseUrl: '@',
		privacyPolicyUrl: '@',

		// GA tag for completed registration
		gtagFullRegistration: '@',

		// Events
		onSuccess: '&',
		onClose: '&'
	},
	controller: ['$element', '$scope', '$window', '$cookies', '$http', '$timeout', '$interval', '$sce', 'patterns', function (
		$element, $scope, $window, $cookies, $http, $timeout, $interval, $sce, patterns) {

		var ctrl = this;

		// Single unified flow:
		//   Step 1 (FORM, userExists=null): user enters email, submits.
		//     -> POST /rpc/magic-link-request
		//     -> status:true   → existing user → CONFIRMATION (sends magic link)
		//     -> status:false  → new user → reveal name field inline (still FORM)
		//   Step 2 (FORM, userExists=false): name appears, user submits both.
		//     -> POST /rpc/quick-registration → reload (logged in)
		ctrl.STEPS = { FORM: 'form', CONFIRMATION: 'confirmation' };

		ctrl.activeStep = ctrl.STEPS.FORM;
		ctrl.userExists = null;        // null = unknown, true = exists, false = new
		ctrl.user = { email: '', name: '' };
		ctrl.inProgress = false;
		ctrl.socialInProgress = false;
		ctrl.error = null;
		ctrl.fieldErrors = {};
		ctrl.confirmationEmail = '';
		ctrl.resendCooldown = 0;
		ctrl._resendTimer = null;

		// Mirror the legacy <registration> validators (resources/common/constants.js).
		ctrl.emailPattern    = patterns.emailPattern;
		ctrl.fullnamePattern = patterns.fullnamePattern;

		// ---------- main submit ----------

		ctrl.submit = function () {
			if (ctrl.inProgress) return;

			var email = (ctrl.user.email || '').trim().toLowerCase();
			ctrl.user.email = email;

			if (!email || !ctrl.emailPattern.test(email)) {
				ctrl.fieldErrors = { email: 'Please enter a valid email address.' };
				return;
			}

			ctrl.fieldErrors = {};
			ctrl.error = null;

			// Step 1: email is unknown — ask BE if user exists by attempting
			// magic-link-request. status:true → exists (send link), status:false
			// → new user (reveal name field inline for signup).
			if (ctrl.userExists === null) {
				ctrl._checkAndRouteEmail(email);
				return;
			}

			// Step 2: BE said the user is new and we collected a name — register.
			if (ctrl.userExists === false) {
				ctrl._submitSignUp(email);
			}
		};

		ctrl._checkAndRouteEmail = function (email) {
			ctrl.inProgress = true;

			ctrl._withRecaptcha(function () {
				// Forward the same alert-tokens the signup path sends so an
				// existing user signing in via magic-link gets the same
				// post-auth side-effects (save listing/building/search).
				// They're embedded in the issued JWT and replayed by
				// ParticleTkn::authenticate after a successful login —
				// HUsr::applyAlertTokens is the shared consumer on both paths.
				var payload = {
					email: email,
					reCaptchaTokens: ctrl._captchaTokens()
				};
				if (ctrl.buildingToken)               payload.buildingAlertToken = ctrl.buildingToken;
				if (ctrl.listingToken)                payload.listingAlertToken = ctrl.listingToken;
				if (ctrl.listingAlertOnBuildingToken) payload.listingAlertOnBuildingToken = ctrl.listingAlertOnBuildingToken;
				if (ctrl.searchToken)                 payload.searchAlertToken = ctrl.searchToken;

				// magic-link-request returns:
				//   { data: { status: true } }  → user exists, link sent
				//   { data: { status: false } } → user not found
				// Our _post helper rejects on status:false; we treat that as the
				// "new user" branch rather than an error.
				ctrl._post(ctrl.magicLinkUrl, payload).then(function () {
					ctrl.inProgress = false;
					ctrl.userExists = true;
					ctrl._showConfirmation(email);
				}, function (err) {
					ctrl.inProgress = false;
					// status:false from a successful HTTP response = "no account".
					// _post rejects with code:'magic_link_no_account' below; any
					// other rejection is a real error.
					if (err && err.code === 'magic_link_no_account') {
						ctrl.userExists = false;
						// name-upfront (CITY-5339): the name was collected with the
						// email, so finish the signup without a second Continue.
						if (ctrl.nameUpfront && ctrl.fullnamePattern.test((ctrl.user.name || '').trim())) {
							ctrl._submitSignUp(email);
							return;
						}
						$timeout(function () { ctrl._focus('name'); }, 50);
					} else {
						ctrl.error = (err && err.message) || 'Could not check your email. Please try again.';
					}
				});
			});
		};

		// ---------- signup (creates user, auto-logs in) ----------

		ctrl._submitSignUp = function (email) {
			var name = (ctrl.user.name || '').trim();
			ctrl.user.name = name;

			if (!ctrl.fullnamePattern.test(name)) {
				ctrl.fieldErrors = { name: 'Please enter a full name.' };
				$timeout(function () { ctrl._focus('name'); }, 50);
				return;
			}

			ctrl.inProgress = true;

			ctrl._withRecaptcha(function () {
				var parts = ctrl._splitName(name);
				var payload = ctrl._buildPayload({
					email: email,
					first_name: parts.first,
					last_name: parts.last,
					reCaptchaTokens: ctrl._captchaTokens()
				});

				ctrl._post(ctrl.quickRegistrationUrl, payload).then(function (data) {
					$cookies.put('crSignup', true, { path: '/' });
					ctrl._trackGtagSuccess();
					ctrl._track('qr-signup-success');
					ctrl.onSuccess({ data: data });
					ctrl._reload(data && data.redirectTo);
				}, function (err) {
					ctrl.inProgress = false;
					if (err && err.fieldErrors) {
						ctrl.fieldErrors = err.fieldErrors;
					} else {
						ctrl.error = (err && err.message) || 'Could not create your account. Please try again.';
					}
				});
			});
		};

		// ---------- confirmation screen ----------

		ctrl._showConfirmation = function (email) {
			ctrl.confirmationEmail = email;
			ctrl.activeStep = ctrl.STEPS.CONFIRMATION;
			ctrl._startResendCooldown();
			ctrl._track('qr-magic-link-sent');
		};

		ctrl.resendLink = function () {
			if (ctrl.inProgress || ctrl.resendCooldown > 0 || !ctrl.confirmationEmail) return;
			ctrl._track('qr-magic-link-resent');
			ctrl._checkAndRouteEmail(ctrl.confirmationEmail);
		};

		// 30-second cooldown matches legacy <registration> resend gating
		// and the BE rate-limit window on /rpc/magic-link-request.
		ctrl._startResendCooldown = function () {
			ctrl.resendCooldown = 30;
			if (ctrl._resendTimer) $interval.cancel(ctrl._resendTimer);
			ctrl._resendTimer = $interval(function () {
				ctrl.resendCooldown--;
				if (ctrl.resendCooldown <= 0) {
					$interval.cancel(ctrl._resendTimer);
					ctrl._resendTimer = null;
				}
			}, 1000);
		};

		ctrl.useDifferentEmail = function () {
			ctrl.activeStep = ctrl.STEPS.FORM;
			ctrl.userExists = null;
			ctrl.user.email = '';
			ctrl.user.name = '';
			ctrl.confirmationEmail = '';
			ctrl.fieldErrors = {};
			ctrl.error = null;
			$timeout(function () {
				ctrl._focus('email');
				// The form's ng-if just remounted the FORM DOM — Google's
				// rendered iframe was destroyed with the previous instance.
				// Re-render into the fresh slot.
				ctrl._renderGoogleButton();
			}, 50);
		};

		// If the user edits the email after the inline name field has appeared,
		// fall back to the email-check step. The name field hides until the
		// BE confirms the new email is also new.
		ctrl.onEmailChange = function () {
			if (ctrl.userExists !== null) {
				ctrl.userExists = null;
				// In name-upfront mode the name field stays visible, so wiping it
				// would silently clear a value the user can still see on screen.
				if (!ctrl.nameUpfront) {
					ctrl.user.name = '';
				}
				ctrl.fieldErrors = {};
				ctrl.error = null;
			}
		};

		// ---------- social auth (all routed through /rpc/quick-registration) ----------

		// Google Identity Services (GIS) — single initialize call that powers
		// both One-Tap and the rendered Continue-with-Google button. The
		// deprecated gapi.auth2 SDK is no longer loaded on this site, so we
		// use the official GIS button rendered by google.accounts.id.renderButton
		// into the qr-google-slot placeholder.
		ctrl.googleButtonId = 'qr-google-signin-' + Math.random().toString(36).slice(2, 10);
		ctrl._gisInitialized = false;

		ctrl._gisCredentialCallback = function (response) {
			if (!response || !response.credential) return;
			// CITY-5603 — drop a credential that arrives while a registration
			// POST is already in flight. The duplicate reached the backend, lost
			// the per-email registration lock (LegacyFunctions::lockEmail) and
			// came back as a generic error (Sentry 983626) although the first
			// request had already created the account. Same guard appleLogin() has.
			if (ctrl.socialInProgress || ctrl.inProgress) return;
			// CITY-5469 — GIS allows a single initialize() per page, shared by
			// the rendered button and One Tap, so the source is derived from
			// the credential's select_by: 'btn*' values mean the rendered
			// button; everything else (auto, user, user_1tap, user_2tap,
			// fedcm, fedcm_auto, itp, ...) is the One Tap prompt. A missing
			// select_by conservatively counts as the button.
			var isFromOneTap = !!(response.select_by && response.select_by.indexOf('btn') !== 0);
			ctrl._track('qr-social-google');
			ctrl.socialInProgress = true;
			ctrl._socialPost({
				// Root copy is what HRegistration::extractIsFromOneTap reads;
				// the nested one keeps the Google credential self-describing.
				isFromOneTap: isFromOneTap,
				google: {
					id_token: response.credential,
					userInfo: {},
					isFromOneTap: isFromOneTap
				}
			});
			$scope.$applyAsync();
		};

		ctrl._gisEnsureInit = function () {
			if (ctrl._gisInitialized) return true;
			if (typeof google === 'undefined' || !google.accounts || !google.accounts.id) return false;
			if (!ctrl.googleClientId) return false;
			google.accounts.id.initialize({
				client_id: ctrl.googleClientId,
				cancel_on_tap_outside: false,
				callback: ctrl._gisCredentialCallback
			});
			ctrl._gisInitialized = true;
			return true;
		};

		// Render Google's official button into qr-google-slot. Retries until
		// the GIS SDK and the slot DOM element are both ready.
		//
		// The retry is bounded and skipped entirely when there is no Google
		// button to render: _gisEnsureInit() returns false forever without a
		// client id, so an instance with social disabled (CITY-5339 inline
		// form) would otherwise schedule a $digest every 200ms for the life of
		// the page once useDifferentEmail() called this.
		ctrl._googleRenderAttempts = 0;

		ctrl._renderGoogleButton = function () {
			if (!ctrl.googleClientId || ctrl.showSocial === false) return;
			if (++ctrl._googleRenderAttempts > 50) return; // ~10s
			if (!ctrl._gisEnsureInit()) {
				$timeout(ctrl._renderGoogleButton, 200);
				return;
			}
			var slot = document.getElementById(ctrl.googleButtonId);
			if (!slot) {
				$timeout(ctrl._renderGoogleButton, 200);
				return;
			}
			ctrl._googleRenderAttempts = 0;
			// Render at full slot width (clamped to GIS's 400px ceiling).
			// logo_alignment:'center' groups the G logo and "Continue with
			// Google" label as a single centered unit inside the iframe —
			// matches Apple which also centers its icon+label group
			// (see .qr-social-button: justify-content:center). Both rows
			// then read as a consistent "[icon] Continue with X" centered.
			var width = Math.min(400, slot.offsetWidth || 400);
			google.accounts.id.renderButton(slot, {
				type:  'standard',
				theme: 'outline',
				size:  'large',
				text:  'continue_with',
				shape: 'rectangular',
				logo_alignment: 'center',
				width: width
			});
		};

		// Apple — mirrors Catalin's pattern in registration.js (commit 25edaeafec).
		// Lazy SDK init on first click. Server re-verifies the id_token in
		// Validators::appleDataCheck against the per-tab nonce held in
		// $_SESSION['apple_signin_nonces'] (multi-tab safe — CITY-5336).
		ctrl._appleInitialized = false;
		ctrl._appleEnsureInit = function () {
			if (ctrl._appleInitialized) return true;
			if (typeof window.AppleID === 'undefined' || !window.AppleID.auth) return false;
			if (!ctrl.appleClientId) return false;
			window.AppleID.auth.init({
				clientId:    ctrl.appleClientId,
				scope:       'name email',
				redirectURI: window.location.origin + (ctrl.appleRedirectPath || '/auth/apple/callback'),
				usePopup:    true,
				nonce:       ctrl.appleNonce
			});
			ctrl._appleInitialized = true;
			return true;
		};

		ctrl.appleLogin = function () {
			if (ctrl.socialInProgress) return;
			ctrl._track('qr-social-apple');
			if (!ctrl._appleEnsureInit()) return;

			ctrl.socialInProgress = true;
			window.AppleID.auth.signIn().then(function (resp) {
				if (!resp || !resp.authorization || !resp.authorization.id_token) {
					ctrl.socialInProgress = false;
					$scope.$applyAsync();
					return;
				}
				ctrl._socialPost({
					apple: {
						id_token: resp.authorization.id_token,
						user:     resp.user || null  // first-login-only per Apple's contract
					}
				});
				$scope.$applyAsync();
			}).catch(function () {
				// popup_closed_by_user, etc. — clear the spinner so they can retry
				ctrl.socialInProgress = false;
				$scope.$applyAsync();
			});
		};

		ctrl._socialPost = function (extra) {
			ctrl._withRecaptcha(function () {
				var payload = ctrl._buildPayload(angular.extend({
					reCaptchaTokens: ctrl._captchaTokens()
				}, extra));

				ctrl._post(ctrl.quickRegistrationUrl, payload).then(function (data) {
					$cookies.put('crSignup', true, { path: '/' });
					ctrl._trackGtagSuccess();
					ctrl._track('qr-signup-success');
					ctrl.onSuccess({ data: data });
					ctrl._reload(data && data.redirectTo);
				}, function (err) {
					ctrl.socialInProgress = false;
					ctrl.error = (err && err.message) || 'Sign-in failed. Please try again.';
				});
			});
		};

		// ---------- One-Tap ----------
		// Reuses the same initialize() call as the rendered button (GIS allows
		// only one initialize per page). One-Tap is opt-in via oneTapEnabled.

		ctrl._oneTapPrompt = function () {
			if (!ctrl._gisEnsureInit()) {
				$timeout(ctrl._oneTapPrompt, 200);
				return;
			}
			google.accounts.id.prompt();
		};

		ctrl.close = function () {
			ctrl.onClose();
		};

		// ---------- helpers ----------

		ctrl._splitName = function (full) {
			var parts = full.split(/\s+/);
			return {
				first: parts.shift() || '',
				last:  parts.join(' ') || ''
			};
		};

		ctrl._buildPayload = function (extra) {
			var payload = {
				src: ctrl.paramSrc,
				tier: ctrl.paramTier,
				kw: ctrl.paramKw,
				listingMaskId: ctrl.listingMaskId,
				buildingId: ctrl.buildingId,
				historyId: ctrl.historyId,
				articleId: ctrl.articleId,
				regPage: $window.location.href
			};

			if (ctrl.regTrigger) {
				payload.regTrigger = ctrl.pageId
					? ctrl.pageId + '-' + ctrl.regTrigger
					: ctrl.regTrigger;
			}

			if (ctrl.regVersion) payload.version = ctrl.regVersion;
			if (ctrl.detectedUserType) payload.buy_or_rent = ctrl.detectedUserType;
			if (ctrl.registrationPageType) payload.reg_page_type = ctrl.registrationPageType;
			if (ctrl.buildingToken) payload.buildingAlertToken = ctrl.buildingToken;
			if (ctrl.listingToken) payload.listingAlertToken = ctrl.listingToken;
			if (ctrl.listingAlertOnBuildingToken) payload.listingAlertOnBuildingToken = ctrl.listingAlertOnBuildingToken;
			if (ctrl.searchToken) payload.searchAlertToken = ctrl.searchToken;
			if (ctrl.calloutBoxType) payload.callOutBoxType = ctrl.calloutBoxType;
			if (ctrl.calloutBoxSourcePage) payload.calloutBoxSourcePage = ctrl.calloutBoxSourcePage;

			angular.extend(payload, extra || {});

			Object.keys(payload).forEach(function (k) {
				if (payload[k] === undefined || payload[k] === null || payload[k] === '') {
					delete payload[k];
				}
			});

			return payload;
		};

		ctrl._post = function (url, payload) {
			if (!url) {
				return Promise.reject({ message: 'Configuration error.' });
			}

			return $http({
				method: 'POST',
				url: url,
				data: payload,
				headers: { 'X-Requested-With': 'XMLHttpRequest' }
			}).then(function (response) {
				var d = response.data || {};
				// Yii responses come wrapped as { data: { status, ... } }
				var inner = d.data || d;
				if (inner && inner.status === false) {
					var extracted = ctrl._extractError(inner);
					// magic-link-request returns a bare { status: false } when
					// the email isn't registered. No form errors in that case →
					// surface a distinct code for the caller to branch on.
					if (url === ctrl.magicLinkUrl && !extracted.fieldErrors && !inner.message) {
						extracted.code = 'magic_link_no_account';
					}
					return Promise.reject(extracted);
				}
				return inner;
			}, function () {
				return Promise.reject({ message: 'Network error. Please try again.' });
			});
		};

		ctrl._extractError = function (data) {
			var fieldErrors = null;
			var firstMessage = null;
			var code = null;

			if (data && data.form && typeof data.form === 'object') {
				fieldErrors = {};
				for (var k in data.form) {
					var v = data.form[k];
					var msg = Array.isArray(v) ? v[0] : (typeof v === 'string' ? v : null);
					if (msg) {
						fieldErrors[k] = msg;
						if (!firstMessage) firstMessage = msg;
						if (k === 'email' && /already|exist|registered|use/i.test(msg)) {
							code = 'email_exists';
						}
					}
				}
				if (Object.keys(fieldErrors).length === 0) fieldErrors = null;
			}

			return {
				message: firstMessage || (data && data.message) || 'Something went wrong. Please try again.',
				fieldErrors: fieldErrors,
				code: code
			};
		};

		ctrl._captchaTokens = function () {
			if (window.crRecaptcha && typeof window.crRecaptcha.getTokens === 'function') {
				return window.crRecaptcha.getTokens();
			}
			return null;
		};

		ctrl._withRecaptcha = function (callback) {
			if (window.crRecaptcha && typeof window.crRecaptcha.execute === 'function') {
				// 3rd arg execV2 omitted (defaults to true) — RpcQuickRegistrationAction
				// validates `reCaptchaTokens.v2`, so we must run the invisible V2
				// challenge to populate getTokens().v2. Passing false here
				// skipped V2 entirely and the BE rejected with "Recaptcha key missing".
				window.crRecaptcha.execute(function () {
					callback();
					$scope.$applyAsync();
				}, 'quickRegistration');
			} else {
				callback();
			}
		};

		ctrl._reload = function (url) {
			if (url) {
				$window.location.href = url;
			} else {
				$window.location.reload();
			}
		};

		ctrl._focus = function (field) {
			var el = $element[0].querySelector('[data-field="' + field + '"]');
			if (el && typeof el.focus === 'function') el.focus();
		};

		ctrl._trackGtagSuccess = function () {
			if (ctrl.gtagFullRegistration && typeof crGtag !== 'undefined') {
				try { crGtag.track(ctrl.gtagFullRegistration); } catch (e) { /* swallow */ }
			}
		};

		// Lightweight GA event tracking. Funnel visibility per CITY-5336:
		// qr-opened, qr-social-{provider}, qr-magic-link-{sent,resent},
		// qr-signup-success.
		ctrl._track = function (event) {
			if (typeof crGtag !== 'undefined') {
				try { crGtag.track(event); } catch (e) { /* swallow */ }
			}
		};

		// ---------- public API for the popup wrapper ----------

		ctrl.reset = function () {
			var wasConfirmation = ctrl.activeStep === ctrl.STEPS.CONFIRMATION;
			ctrl.activeStep = ctrl.STEPS.FORM;
			ctrl.userExists = null;
			ctrl.user = { email: '', name: '' };
			ctrl.inProgress = false;
			ctrl.socialInProgress = false;
			ctrl.fieldErrors = {};
			ctrl.error = null;
			ctrl.confirmationEmail = '';
			ctrl.resendCooldown = 0;
			if (ctrl._resendTimer) {
				$interval.cancel(ctrl._resendTimer);
				ctrl._resendTimer = null;
			}
			// If we were on CONFIRMATION, ng-if just remounted the FORM DOM —
			// the Google iframe rendered into the previous slot was destroyed
			// with it. Re-render into the fresh slot so the next open() shows
			// a populated button instead of an empty bordered box.
			if (wasConfirmation) {
				$timeout(ctrl._renderGoogleButton, 50);
			}
		};

		// ---------- lifecycle ----------

		ctrl.$onInit = function () {
			ctrl.subtitleHtml = $sce.trustAsHtml(ctrl.subtitle || '');

			// Render Google's official sign-in button (replaces the legacy
			// gapi.auth2 popup which is no longer available).
			if (ctrl.googleClientId && ctrl.showSocial !== false) {
				$timeout(ctrl._renderGoogleButton, 0);
			}

			if (ctrl.googleClientId && ctrl.oneTapEnabled) {
				$timeout(ctrl._oneTapPrompt, 800);
			}
		};

		ctrl.$onChanges = function (changes) {
			if (changes.subtitle) {
				ctrl.subtitleHtml = $sce.trustAsHtml(changes.subtitle.currentValue || '');
			}
		};

		ctrl.$onDestroy = function () {
			if (ctrl._resendTimer) {
				$interval.cancel(ctrl._resendTimer);
				ctrl._resendTimer = null;
			}
		};
	}]
});
